import apiService, { ApiError } from './apiService';

/**
 * Merchant Service
 * Handles all merchant-related API operations
 */
class MerchantService {
  /**
   * Format date to API required format (YYYY-MM-DD HH:mm:ss)
   * @param {string|Date} date - Date to format
   * @param {boolean} isEndDate - Whether this is end date (adds 23:59:59)
   * @returns {string} Formatted date string
   */
  formatDateForAPI(date, isEndDate = false) {
    if (!date) return null;

    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return null;

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    const time = isEndDate ? '23:59:59' : '00:00:00';

    return `${year}-${month}-${day} ${time}`;
  }

  /**
   * Get list of merchants with pagination
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 20)
   * @param {string} params.keyword - Search keyword (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Merchant list response
   */
  async getMerchantList(params = {}, options = {}) {
    const { page = 1, size = 20, keyword, dateFrom, dateTo, order } = params;

    try {
      // Build request data
      const requestData = {
        page,
        size,
      };

      // Add keyword to payload if provided
      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }

      // Add order if provided
      if (order) {
        requestData.order = order;
      }

      // Add dateFilter to payload if dates are provided
      if (dateFrom && dateTo) {
        const startDate = this.formatDateForAPI(dateFrom, false);
        const endDate = this.formatDateForAPI(dateTo, true);

        if (startDate && endDate) {
          requestData.dateFilter = {
            start: startDate,
            end: endDate,
          };
        }
      }

      const response = await apiService.skaiOneRequest('/api/v1/partner/list', {
        method: 'POST',
        data: requestData,
        ...options,
      });

      // Validate response structure
      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from merchant list API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch merchant list:', error);

      // Re-throw with additional context for merchant-specific errors
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle unexpected errors
      throw new ApiError(
        'Failed to retrieve merchant list',
        0,
        'MERCHANT_LIST_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get merchant details by ID
   * @param {string} merchantId - Merchant ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Merchant details response
   */
  async getMerchantDetails(merchantId, options = {}) {
    if (!merchantId) {
      throw new ApiError('Merchant ID is required', 400, 'MISSING_MERCHANT_ID');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/partner/${merchantId}`,
        {
          method: 'GET',
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(
        `Failed to fetch merchant details for ID ${merchantId}:`,
        error
      );

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve merchant details',
        0,
        'MERCHANT_DETAILS_ERROR',
        { merchantId, originalError: error.message }
      );
    }
  }

  /**
   * Search merchants by criteria
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.query - Search query (name, email, etc.)
   * @param {number} searchParams.page - Page number (default: 1)
   * @param {number} searchParams.size - Page size (default: 20)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Search results response
   */
  async searchMerchants(searchParams = {}, options = {}) {
    const { query, page = 1, size = 20 } = searchParams;

    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/partner/search',
        {
          method: 'POST',
          data: {
            query,
            page,
            size,
          },
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to search merchants:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to search merchants',
        0,
        'MERCHANT_SEARCH_ERROR',
        { searchParams, originalError: error.message }
      );
    }
  }

  /**
   * Search merchants for admin with keyword
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.keyword - Search keyword
   * @param {number} searchParams.page - Page number (default: 1)
   * @param {number} searchParams.size - Page size (default: 20)
   * @param {string} searchParams.order - Order parameter (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Search results response
   */
  async searchMerchantsAdmin(searchParams = {}, options = {}) {
    const { keyword = '', page = 1, size = 20, order = '' } = searchParams;

    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/partner/search-admin',
        {
          method: 'POST',
          data: {
            keyword,
            page,
            size,
            order,
          },
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to search merchants (admin):', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to search merchants (admin)',
        0,
        'MERCHANT_SEARCH_ADMIN_ERROR',
        { searchParams, originalError: error.message }
      );
    }
  }

  /**
   * Transform API merchant data to frontend format
   * @param {Object} apiMerchant - Raw merchant data from API
   * @returns {Object} Transformed merchant data
   */
  transformMerchantData(apiMerchant) {
    if (!apiMerchant) return null;

    return {
      id: apiMerchant.id,
      accountId: apiMerchant.accountId,
      companyName: apiMerchant.name || 'N/A',
      email: apiMerchant.email || 'N/A',
      phone: apiMerchant.phone || 'N/A',
      address: apiMerchant.address || 'N/A',
      zip: apiMerchant.zip || 'N/A',
      sector: apiMerchant.categoryName || 'N/A',
      categoryId: apiMerchant.categoryId,
      country: apiMerchant.countryName || 'N/A', // This might need country name lookup
      status: apiMerchant.status || 'N/A',
      balance: apiMerchant.balance || 0,
      careerPoint: Math.round(apiMerchant.careerPoint || 0),
      joinDate: apiMerchant.createdAt
        ? new Date(apiMerchant.createdAt).toLocaleDateString('en-US')
        : 'N/A',
      lastModified: apiMerchant.modifiedAt
        ? new Date(apiMerchant.modifiedAt).toLocaleDateString('en-US')
        : 'N/A',
      referralCode: apiMerchant.referral || 'N/A',
      downline: apiMerchant.downline || 0,
      partner: apiMerchant.partner || 0,
      paidStart: apiMerchant.paidStart,
      paidExpired: apiMerchant.paidExpired,
      headQuarter: apiMerchant.headQuarter,
      googleID: apiMerchant.googleID,
      voucher: apiMerchant.voucher,
      marginId: apiMerchant.marginId,
      lat: apiMerchant.lat || 0,
      lon: apiMerchant.lon || 0,
      // Add computed fields
      isActive: apiMerchant.status === 'A',
      isPaid:
        apiMerchant.paidExpired &&
        new Date(apiMerchant.paidExpired) > new Date(),
      hasBalance: (apiMerchant.balance || 0) > 0,
      createdAt: apiMerchant.createdAt,
      modifiedAt: apiMerchant.modifiedAt,
    };
  }

  /**
   * Get formatted merchant list with transformed data
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 20)
   * @param {string} params.keyword - Search keyword (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Formatted merchant list response
   */
  async getFormattedMerchantList(params = {}, options = {}) {
    try {
      console.log('Fetching merchants with params:', params);
      const response = await this.getMerchantList(params, options);

      // Transform the merchant data
      const transformedData = {
        ...response.data,
        data: response.data.data.map(merchant =>
          this.transformMerchantData(merchant)
        ),
      };

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error('Failed to get formatted merchant list:', error);
      throw error;
    }
  }

  /**
   * Get formatted merchant details by ID
   * @param {string} merchantId - Merchant ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Formatted merchant details
   */
  async getFormattedMerchantDetails(merchantId, options = {}) {
    try {
      const response = await this.getMerchantDetails(merchantId, options);

      // Transform the merchant data
      const transformedData = this.transformMerchantData(response.data);

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error(
        `Failed to get formatted merchant details for ID ${merchantId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Create a new merchant
   * @param {Object} merchantData - Merchant data to create
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Created merchant response
   */
  async createMerchant(merchantData, options = {}) {
    try {
      const response = await apiService.skaiOneRequest('/api/v1/partner', {
        method: 'POST',
        data: merchantData,
        ...options,
      });

      return response;
    } catch (error) {
      console.error('Failed to create merchant:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to create merchant',
        0,
        'MERCHANT_CREATE_ERROR',
        { merchantData, originalError: error.message }
      );
    }
  }

  /**
   * Update merchant information
   * @param {string} merchantId - Merchant ID
   * @param {Object} updateData - Data to update
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Updated merchant response
   */
  async updateMerchant(merchantId, updateData, options = {}) {
    if (!merchantId) {
      throw new ApiError('Merchant ID is required', 400, 'MISSING_MERCHANT_ID');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/partner/${merchantId}`,
        {
          method: 'PUT',
          data: updateData,
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to update merchant ${merchantId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to update merchant',
        0,
        'MERCHANT_UPDATE_ERROR',
        { merchantId, updateData, originalError: error.message }
      );
    }
  }

  /**
   * Delete/deactivate a merchant
   * @param {string} merchantId - Merchant ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Delete response
   */
  async deleteMerchant(merchantId, options = {}) {
    if (!merchantId) {
      throw new ApiError('Merchant ID is required', 400, 'MISSING_MERCHANT_ID');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/partner/${merchantId}`,
        {
          method: 'DELETE',
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to delete merchant ${merchantId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to delete merchant',
        0,
        'MERCHANT_DELETE_ERROR',
        { merchantId, originalError: error.message }
      );
    }
  }

  /**
   * Top up merchant balance
   * @param {Object} topupData - Top-up data
   * @param {string} topupData.merchantId - Merchant ID to top up
   * @param {number} topupData.amount - Amount to top up
   * @param {string} topupData.currency - Currency (default: 'EUR')
   * @param {string} topupData.reference - Reference note for the top-up
   * @param {boolean} topupData.confirm - Confirm the top-up (default: true)
   * @param {boolean} topupData.preview - Preview mode (default: false)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Top-up response
   */
  async topupBalance(topupData, options = {}) {
    const {
      merchantId,
      amount,
      currency = 'EUR',
      reference,
      confirm = true,
      preview = false,
    } = topupData;

    if (!merchantId) {
      throw new ApiError('Merchant ID is required', 400, 'MISSING_MERCHANT_ID');
    }

    if (!amount || amount <= 0) {
      throw new ApiError(
        'Valid positive amount is required',
        400,
        'INVALID_AMOUNT'
      );
    }

    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/partner/topup',
        {
          method: 'POST',
          data: {
            merchantId,
            amount,
            currency,
            reference,
            confirm,
            preview,
          },
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to top up merchant ${merchantId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to top up merchant balance',
        0,
        'MERCHANT_TOPUP_ERROR',
        { topupData, originalError: error.message }
      );
    }
  }

  /**
   * Get balance history for a merchant
   * @param {Object} params - Balance history parameters
   * @param {string} params.companyId - Company/Merchant ID (optional)
   * @param {string} params.type - Transaction type (default: 'N')
   * @param {string} params.startDate - Start date in YYYY-MM-DD format (optional)
   * @param {string} params.endDate - End date in YYYY-MM-DD format (optional)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 20)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Balance history response
   */
  async balanceHistory(params = {}, options = {}) {
    const {
      keyword = '',
      type = 'N',
      startDate,
      endDate,
      page = 1,
      size = 20,
    } = params;

    try {
      const requestData = {
        keyword,
        type,
        page,
        size,
      };

      if (startDate) {
        requestData.startDate = startDate;
      }

      if (endDate) {
        requestData.endDate = endDate;
      }

      const response = await apiService.skaiOneRequest(
        '/api/v1/partner/balance/history',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from balance history API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch balance history:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve balance history',
        0,
        'BALANCE_HISTORY_ERROR',
        { params, originalError: error.message }
      );
    }
  }
}

// Create singleton instance
const merchantService = new MerchantService();

export default merchantService;
export { MerchantService };
